import {useAuth} from 'hooks/useAuth';
import {api, getCurrentUserType} from './common';
import {cleanObjectNilValue} from 'utils/clean';
import {useAdminProfile, useProfile} from './auth';
import {get, isEmpty, isNil, keyBy, keys, some, split} from 'lodash';
import {useTranslation} from 'react-i18next';
import {useUIStore} from 'stores/uiStore';
import {useSWRTemplate} from 'hooks/dataHooksTemplate';
import {processData} from './adminPermission';
import {processUserProfile} from '../../data/userProfile';
import {processPerson} from '../../data/person';
import {adminClient} from '../../helper/httpClient';
import {useCallback, useMemo} from 'react';

const processSub = (item) => {
    if (item?.children) {
        return {
            ...item,
            children: keyBy(item.children.map(processSub), 'code'),
        };
    } else {
        return item;
    }
};

export const updatePassword_ = async ({oldPassword, password}) => {
    const {result} = await api().post(['password'], {
        oldPassword,
        password,
    });
    return result;
};

export const useUserPreference = () => {
    const {isUser} = useAuth();
    const swr = (isUser ? useProfile : useAdminProfile)();
    const {i18n} = useTranslation('app');
    const [firstLanguageCounter, setFirstLanguageCounter] = useUIStore(
        (state) => [state.firstLanguageCounter, state.setFirstLanguageCounter],
    );
    return {
        ...swr,
        data: get(swr, 'data.preference'),
        twoFactorEnabled: Boolean(get(swr, 'data.preference.twoFactor')),
        init: () => {
            if (
                !swr.isLoading &&
                !isEmpty(swr.data) &&
                firstLanguageCounter === 1
            ) {
                if (get(swr, 'data.preference.locale') !== i18n.language) {
                    i18n.changeLanguage(get(swr, 'data.preference.locale'));
                }
                setFirstLanguageCounter(0);
            }
        },
    };
};

export const updatePhishingCode_ = async (id, data) => {
    const {result} = await api().put(['profile', id], data);
    return result;
}

export const updateUserSettings_ = async (payload) => {
    const userTypes = getCurrentUserType();
    const {result} = await api().post(
        [
            userTypes.isAdmin || userTypes.isSupport
                ? 'owner-setting'
                : 'setting',
            'base',
        ],
        payload,
    );
    return result;
};

export const updateUserAvatar_ = async ({avatar}) => {
    const userTypes = getCurrentUserType();
    const {result} = await api().post(
        [
            userTypes.isAdmin || userTypes.isSupport
                ? 'owner-setting'
                : 'setting',
            'avatar',
        ],
        {avatar},
    );
    return result;
};

export const updateContact_ = async (id, payload) => {
    const {result} = await api().put(
        ['profile', id],
        cleanObjectNilValue(payload),
    );
    return result;
};

export const useIbSettings = () => {
    return useSWRTemplate({
        key: ['ib-settings'],
        request: async () => {
            const {result} = await api().get(['setting', 'ib']);
            return result;
        },
    });
};

export const useBasicSetting = () => {
    const swr = useSWRTemplate({
        key: ['ib-settings'],
        request: async () => {
            const {result} = await api().get(['setting', 'basic-setting']);
            return result;
        },
    });
    return {
        ...swr,
        updateBasicSetting: async (id, data) => {
            const {result} = await api().put(
                ['setting', 'basic-setting', id],
                data,
            );
            return result;
        },
    };
};

export const useKycSetting = () => {
    const swr = useSWRTemplate({
        key: ['kyc-settings'],
        request: async () => {
            const {result} = await api().get(['setting', 'kyc']);
            return result;
        },
    });
    return {
        ...swr,
        updateKycSetting: async (data) => {
            const {result} = await api().put(
                ['setting', 'kyc', 'KYC_DEFAULT_SETTINGS'],
                data,
            );
            return result;
        },
    };
};

export const useMaintenanceSetting = () => {
    const swr = useSWRTemplate({
        key: ['maintenance-settings'],
        request: async () => {
            const {result} = await api().get([
                'setting',
                'maintenance-setting',
            ]);
            return result;
        },
    });
    return {
        ...swr,
        updateMaintenanceSetting: async (data) => {
            const {result} = await api().put(
                ['setting', 'maintenance-setting', 'MTS'],
                data,
            );
            return result;
        },
    };
};

export const useMT4Setting = (type) => {
    const swr = useSWRTemplate({
        key: ['mt4-settings', type],
        request: async () => {
            const {result} = await api().get([
                'setting', 'mt4',
            ], { type });
            return result;
        },
    });
    return {
        ...swr,
        updateMT4Setting: async (data, type) => {
            const {result} = await api().put(
                ['setting', 'mt4', type === 's1'?'MT4_SERVER_SETTINGS': type === 'demo'? 'MT4_DEMO_SERVER_SETTINGS': type === 's3'? 'MT4_S3_SERVER_SETTINGS' : 'MT4_S2_SERVER_SETTINGS'],
                cleanObjectNilValue(data),
            );
            return result;
        },
    };
};

export const useAutoLeverageSetting = (source = 'MT4') => {
    const swr = useSWRTemplate({
        key: ['auto-leverage-setting', source],
        request: async () => {
            const {result} = await api().get(
                ['setting', 'leverage-approval-setting'],
                {source},
            );
            return result;
        },
    });
    return {
        ...swr,
        updateMT45AutoLeverage: async (data) => {
            const {result} = await api().post(
                ['setting', 'leverage-approval-setting'],
                cleanObjectNilValue(data),
            );
            return result;
        },
    };
};

export const useMT5Setting = (options={}) => {
    const {type} = options
    const swr = useSWRTemplate({
        key: ['mt5-settings', type],
        request: async (_, _type) => {
            const {result} = await api().get(['setting', 'mt5'], cleanObjectNilValue({ type: type }));
            return result;
        },
    });
    return {
        ...swr,
        updateMT5Setting: async (data) => {
            const {result} = await api().put(
                ['setting', 'mt5', 'MT5_SERVER_SETTINGS'],
                data,
            );
            return result;
        },
    };
};

export const testConnect = async () => {
    const {result} = await api().post(['test-connect'], {
        host: 'localhost',
        user: 'root',
        password: 'yangleicheng',
    });
    return result;
};

export const usePlatformProduct = () => {
    const swr = useSWRTemplate({
        key: ['platform-product'],
        request: async () => {
            const {result} = await api().get(['platform', 'product']);
            return result;
        },
    });
    return {
        ...swr,
        stockPermissions: (swr.data || []).filter(
            (item) => get(item, 'name') === 'Stock' && get(item, 'enable'),
        ),
    };
};

export const editPlatformProduct_ = async (data) => {
    const {result} = await api().put(
        ['platform', 'product', 'Stock'],
        cleanObjectNilValue(data),
    );
    return result;
};

export const useCompanyUserRoles = () => {
    const swr = useSWRTemplate({
        key: ['company-user-roles'],
        request: async ([_]) => {
            // const {result} = await api().get(['role-type']);

            const result = [
                {
                    "code": "CLIENT_COMPANY_ADMIN",
                    "name": "Client Company Admin",
                    "desc": null,
                    "isDeleted": 0,
                    "deletedAt": null
                },
                {
                    "code": "CLIENT_COMPANY_OPERATOR",
                    "name": "Client Company Operator",
                    "desc": null,
                    "isDeleted": 0,
                    "deletedAt": null
                },
                {
                    "code": "CLIENT_COMPANY_READONLY",
                    "name": "Client Company Readonly",
                    "desc": null,
                    "isDeleted": 0,
                    "deletedAt": null
                },
                {
                    "code": "CLIENT_COMPANY_DEVELOPER",
                    "name": "Client Developer",
                    "desc": null,
                    "isDeleted": 0,
                    "deletedAt": null
                },
            ];

            return result
                .map((item) => {
                    return {
                        ...item,
                        label: item?.code,
                        value: item?.code,
                    };
                });
        },
        defaultValue: [],
    });
    return swr;
};


export const useClientRolesAccessByCode = (role) => {
    const swr = useSWRTemplate({
        key: ['client-roles-access',role],
        request: async ([_]) => {
            // const {result} = await api().get(['permissions',role])
            // return result

            return [
                {
                    code: "home",
                    read: 1,
                    write: 1,
                },
                {
                    code: "myAccount",
                    read: 1,
                    write: 1,
                },
                {
                    code: "funds",
                    read: 1,
                    write: 1,
                },
                {
                    code: 'clientManagement',
                    read: 1,
                    write: 1,
                    children: [
                        {
                            code: 'forgetPassword',
                            read: 1,
                            write: 1,
                            parent: "clientManagement",
                        },
                        {
                            code: 'clientContact',
                            read: 1,
                            write: 1,
                            parent: "clientManagement",
                        },
                        {
                            code: 'clientKyc',
                            read: 1,
                            write: 1,
                            parent: "clientManagement",
                        },
                        {
                            code: 'security',
                            read: 1,
                            write: 1,
                            parent: "clientManagement",
                        },
                    ]

                },
                {
                    code: "ibAgentAndReferrals",
                    read: 1,
                    write: 1,
                },
                {
                    code: "APIs",
                    read: 1,
                    write: 1,
                },
                {
                    code: "about",
                    read: 1,
                    write: 1,
                }
            ]


        },
        defaultValue: [],
    });
    return swr;
};


export const useClientAllPermissions = (data) => {
    const {t} = useTranslation('app');
    const swr = useSWRTemplate({
        key: ['client-all-system-permissions', data],
        request: async ([_]) => {
            // const {result} = await api().get(['permissions'], { role: 'CLIENT_COMPANY_ADMIN' })
            // await sleep_(1000);

            const result = [
                {
                    code: "home",
                    read: 1,
                    write: 1,
                },
                {
                    code: "myAccount",
                    read: 1,
                    write: 1,
                },
                {
                    code: "funds",
                    read: 1,
                    write: 1,
                },
                {
                    code: 'clientManagement',
                    read: 1,
                    write: 1,
                    children: [
                        {
                            code: 'forgetPassword',
                            read: 1,
                            write: 1,
                            parent: "clientManagement",
                        },
                        {
                            code: 'clientContact',
                            read: 1,
                            write: 1,
                            parent: "clientManagement",
                        },
                        {
                            code: 'clientKyc',
                            read: 1,
                            write: 1,
                            parent: "clientManagement",
                        },
                        {
                            code: 'security',
                            read: 1,
                            write: 1,
                            parent: "clientManagement",
                        },
                    ]

                },
                {
                    code: "ibAgentAndReferrals",
                    read: 1,
                    write: 1,
                },
                {
                    code: "APIs",
                    read: 1,
                    write: 1,
                },
                {
                    code: "about",
                    read: 1,
                    write: 1,
                }
            ]



            return processData(data, result, null, t);
        },
        defaultValue: [],
    });
    return swr;
};


export const useInviteCompanyUser = (options = {}) => {
    const {id} = useAuth();
    const swr = useSWRTemplate({
        key: id ? ['invite-company-user', id] : null,
        request: async ([_, _id]) => {
            // const {result} = await api().get(
            //     ['company-user-invitation'],
            // );

            const result = {
                "registered": [
                    {
                        "id": 10034,
                        "firstname": null,
                        "lastname": null,
                        "middlename": null,
                        "legalName": null,
                        "mobile": null,
                        "email": "16d353441f4e@drmail.in",
                        "position": null,
                        "nativeLanguageFirstname": null,
                        "nativeLanguageMiddlename": null,
                        "nativeLanguageLastname": null,
                        "nativeLanguageLegalName": null,
                        "phoneCode": null,
                        "phoneNo": null,
                        "dob": null,
                        "frontPhotoOfPrimary": null,
                        "backPhotoOfPrimary": null,
                        "photoOfSecondary": null,
                        "photoOfAdditional": null,
                        "type": "companyContact",
                        "occupation": null,
                        "kycType": "shareCompany",
                        "kycStatus": "draft",
                        "amlStatus": "unverified",
                        "kycExpireDate": null,
                        "kycRenewExpireDate": null,
                        "reason": null,
                        "isLR": 0,
                        "isAP": 0,
                        "identificationType": "OTHERS",
                        "identificationValue": null,
                        "createdAt": "2025-09-17T02:08:55.000Z",
                        "updatedAt": null,
                        "amlStatusValidTimestamp": null,
                        "amlStatusInvalidTimestamp": null,
                        "amlStatusUnverifiedTimestamp": "2025-09-17T02:08:55.000Z",
                        "amlStatusPendingTimestamp": null,
                        "kycStatusPendingTimestamp": null,
                        "kycStatusVerifiedTimestamp": null,
                        "kycStatusSuspendedTimestamp": null,
                        "kycStatusReturnedTimestamp": null,
                        "kycStatusRenewTimestamp": null,
                        "kycStatusRenewPendingTimestamp": null,
                        "kycStatusDeclinedTimestamp": null,
                        "kycStatusDraftTimestamp": "2025-09-17T02:08:55.000Z",
                        "region": "AU",
                        "title": null,
                        "address": null,
                        "company": 10143,
                        "client": 120,
                        "mccCode": null,
                        "estimate": null,
                        "contact": 20,
                        ":contact": {
                            "id": 20,
                            "email": "16d353441f4e@drmail.in",
                            "mobile": null,
                            "createdAt": "2025-09-17T02:08:55.000Z",
                            "updatedAt": null,
                            "address": null
                        },
                        ":user": {
                            "id": 10021,
                            "name": null,
                            "avatar": null,
                            "password": "XLhF+Qc1OI50mDrEGqYw8SeHsx5EXzL/Q2HS9hnmSD8=",
                            "passwordSalt": "wyTCFKzD",
                            "mobile": null,
                            "email": "16d353441f4e@drmail.in",
                            "status": "activated",
                            "createdAt": "2025-09-17T02:08:55.000Z",
                            "updatedAt": "2025-10-22T03:34:58.000Z",
                            "statusSuspendedTimestamp": null,
                            "statusActivatedTimestamp": "2025-09-17T02:08:55.000Z",
                            "setting": 27,
                            "individual": 10034,
                            "client": 120,
                            ":client": {
                                "id": 120,
                                "clientType": "company",
                                "reference": "L2509174931808",
                                "referralReference": "NpV9cCfhWJdQRwyBScux",
                                "status": "activated",
                                "source": "register",
                                "enableCommission": 0,
                                "checkAml": 0,
                                "checkKyb": 0,
                                "checkKyc": 0,
                                "checkAmlStatus": "unverified",
                                "checkKybStatus": "unverified",
                                "checkKycStatus": "unverified",
                                "createdAt": "2025-09-17T02:08:55.000Z",
                                "updatedAt": "2025-09-17T02:20:15.000Z",
                                "individual": null,
                                "company": 10143,
                                "terms": 10,
                                "ibLevel": null,
                                "riskLevel": "L2",
                                "regulation": 1,
                                "sale": null,
                                "functions": 20,
                                "clientParams": "{\"AW\":{\"status\":\"activated\",\"clientId\":389,\"customerType\":\"company\",\"kycStatus\":\"verified\"}}",
                                ":company": {
                                    "id": 10143,
                                    "name": "Martin Ltd",
                                    "businessNo": "73472824",
                                    "industryName": null,
                                    "officialWebsite": null,
                                    "email": "16d353441f4e@drmail.in",
                                    "logo": null,
                                    "desc": null,
                                    "kycStatus": "verified",
                                    "amlStatus": "unverified",
                                    "kycExpireDate": "2028-09-17T00:00:00.000Z",
                                    "kycRenewExpireDate": null,
                                    "reason": null,
                                    "mobile": "+61410386934",
                                    "phoneCode": "+61",
                                    "phoneNo": "410386934",
                                    "pspParams": null,
                                    "createdAt": "2025-09-17T02:08:55.000Z",
                                    "updatedAt": "2025-09-17T02:18:32.000Z",
                                    "kycStatusPendingTimestamp": "2025-09-17T02:18:11.000Z",
                                    "kycStatusVerifiedTimestamp": "2025-09-17T02:18:33.000Z",
                                    "kycStatusSuspendedTimestamp": null,
                                    "kycStatusReturnedTimestamp": null,
                                    "kycStatusRenewTimestamp": null,
                                    "kycStatusRenewPendingTimestamp": null,
                                    "kycStatusDeclinedTimestamp": null,
                                    "kycStatusDraftTimestamp": "2025-09-17T02:08:55.000Z",
                                    "amlStatusValidTimestamp": null,
                                    "amlStatusInvalidTimestamp": null,
                                    "amlStatusUnverifiedTimestamp": "2025-09-17T02:08:55.000Z",
                                    "amlStatusPendingTimestamp": null,
                                    "industry": "RETAIL",
                                    "region": "AU",
                                    "address": 1359,
                                    "estimate": 11,
                                    "client": 120,
                                    "mccCode": null
                                }
                            },
                            ":individual": {
                                "id": 10034,
                                "firstname": null,
                                "lastname": null,
                                "middlename": null,
                                "legalName": null,
                                "mobile": null,
                                "email": "16d353441f4e@drmail.in",
                                "position": null,
                                "nativeLanguageFirstname": null,
                                "nativeLanguageMiddlename": null,
                                "nativeLanguageLastname": null,
                                "nativeLanguageLegalName": null,
                                "phoneCode": null,
                                "phoneNo": null,
                                "dob": null,
                                "frontPhotoOfPrimary": null,
                                "backPhotoOfPrimary": null,
                                "photoOfSecondary": null,
                                "photoOfAdditional": null,
                                "type": "companyContact",
                                "occupation": null,
                                "kycType": "shareCompany",
                                "kycStatus": "draft",
                                "amlStatus": "unverified",
                                "kycExpireDate": null,
                                "kycRenewExpireDate": null,
                                "reason": null,
                                "isLR": 0,
                                "isAP": 0,
                                "identificationType": "OTHERS",
                                "identificationValue": null,
                                "createdAt": "2025-09-17T02:08:55.000Z",
                                "updatedAt": null,
                                "amlStatusValidTimestamp": null,
                                "amlStatusInvalidTimestamp": null,
                                "amlStatusUnverifiedTimestamp": "2025-09-17T02:08:55.000Z",
                                "amlStatusPendingTimestamp": null,
                                "kycStatusPendingTimestamp": null,
                                "kycStatusVerifiedTimestamp": null,
                                "kycStatusSuspendedTimestamp": null,
                                "kycStatusReturnedTimestamp": null,
                                "kycStatusRenewTimestamp": null,
                                "kycStatusRenewPendingTimestamp": null,
                                "kycStatusDeclinedTimestamp": null,
                                "kycStatusDraftTimestamp": "2025-09-17T02:08:55.000Z",
                                "region": "AU",
                                "title": null,
                                "address": null,
                                "company": 10143,
                                "client": 120,
                                "mccCode": null,
                                "estimate": null,
                                "contact": 20
                            },
                            ":roles": [
                                {
                                    "id": 21,
                                    "createdAt": "2025-09-17T02:08:55.000Z",
                                    "user": 10021,
                                    "role": "CLIENT_COMPANY_ADMIN"
                                },
                                {
                                    "id": 40,
                                    "createdAt": "2025-11-24T10:01:22.000Z",
                                    "user": 10021,
                                    "role": "CLIENT_TEST"
                                }
                            ],
                            ":setting": {
                                "id": 27,
                                "locale": "en-US",
                                "timezone": "Australia/Sydney",
                                "readLog": 0,
                                "isMobileVerified": 0,
                                "isEmailVerified": 0,
                                "twoFactor": 0,
                                "loginTwoFactor": 0,
                                "enableSmsFact": 0,
                                "enableEmailFact": 1,
                                "enableOtpFact": 0,
                                "otpSecret": null,
                                "enableFingerprintFact": null,
                                "enableFaceFact": null,
                                "smsNotification": 0,
                                "emailNotification": 1,
                                "stockColor": "greenUpRedDown",
                                "allowDevelop": 1,
                                "receiveEmail": 1,
                                "canViewUserPrivacy": 0,
                                "requiredUpdatePassword": 0,
                                "requiredUpdateOtp2FA": 0,
                                "antiPhishingCode": "920c7d28ca",
                                "last2faDate": null,
                                "createdAt": "2025-09-17T02:08:55.000Z",
                                "updatedAt": "2025-12-02T11:54:26.000Z",
                                "receiveCommissionCurrency": "USD",
                                "preferredPlatformCurrency": "USD",
                                "preferredPlatformCryptoCurrency": null
                            }
                        }
                    }
                ],
                "inviting": []
            }

            const registered = (result?.registered || []).map((item) => ({
                ...processUserProfile(get(item, ':user')),
                ...processPerson(item),
                "status": "activated",
            }));
            const inviting = result?.inviting || [];
            return [...registered, ...inviting]

        },
        defaultValue: [],
    });
    return swr;
};


export const useClientPermissionSettings = (options = {}) => {
    const {roles = []} = options;
    const swr = useSWRTemplate({
        key: roles?.length  ? ['client-permission-settings']: null,
        request: async ([_]) => {
            let data = {}
            for (const key of roles) {
                const {result} = await api().get(['permissions'], { role: key })
                data[key] = result
            }
            return data
        },
        defaultValue: {},
    });

    const searchOptPermission = useMemo(
        () =>
            keys(get(swr, 'data', {})).reduce((prev, curr) => {
                return {
                    ...prev,
                    [curr]: keyBy(
                        get(swr, `data.${curr}`, []).map(processSub),
                        'code',
                    ),
                };
            }, {}),
        [swr.data],
    );
    return {
        ...swr,
        getPermission: useCallback(
            (key, type = 'read') => {
                return some(
                    roles.map((role) => {
                        const setting = get(searchOptPermission, role);
                        if (isNil(setting)) {
                            return false;
                        }
                        const splitKeys = split(key, '.');
                        const permission = get(
                            setting,
                            `${splitKeys.join('.children.')}.${type}`,
                            false,
                        );
                        return permission;
                    }),
                );
            },
            [searchOptPermission],
        ),
    };
};

export const useCompanyOperatorCount = (options = {}) => {
    const swr = useSWRTemplate({
        key: ['company-operator-count'],
        request: async (_,) => {
            // const {result} = await api().get(['platform', 'setting'],
            //     cleanObjectNilValue({ code: 'MAX_COMPANY_OPERATOR_NUMBER'}));
            // return result;

            return 3;
        },
    });
    return {
        ...swr,
        data: get(swr, 'data') ? Number(get(swr, 'data')) : 0,
    };

};

export const inviteCompanyUser_ = async (payload) => {
    const {result} = await api().post(
        ['company-user-invitation'],
        cleanObjectNilValue(payload),
    );
    return result;
};

export const editCompanyUserStatus_ = async (id, payload) => {
    const {result} = await api().put(
        ['company-user-invitation', id],
        cleanObjectNilValue(payload),
    );
    return result;
};

export const companyUserResetPassword_ = async (data) => {
    const {result} = await api().post(
        ['reset-password'],
        cleanObjectNilValue(data),
    );
    return result;
};
